import { useMemo, useState } from 'react'
import { formatCount } from '@/lib/format'

/** Fixed categorical order (dataviz rule: assign in order, never cycle). 'other' gets its own
 *  reserved neutral - it aggregates the tail, not one version. Status colours stay reserved. */
export const SERIES_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)']
export const OTHER_COLOR = 'var(--chart-other)'

export type DownloadSeries = Array<{ version: string; points: Array<{ date: string; count: number }> }>

/**
 * 90-day per-version downloads line chart with an interactive legend. Series arrive server-capped
 * (top-6 by 90d volume + 'other'); points are sparse and get zero-filled onto a continuous 90-day
 * UTC day axis (never interpolated - a version downloaded on 2 of 90 days must read as spikes,
 * not a connecting ramp). Legend swatches are real buttons (aria-pressed) toggling visibility;
 * hovering an entry dims the other lines. The per-day hit strip drives ONE tooltip listing every
 * visible series' value for that day, sorted descending. No draw animation.
 */
export function DownloadsSeriesChart({ series, builtVersions }: { series: DownloadSeries; builtVersions: Set<string> }) {
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [hovered, setHovered] = useState<number | null>(null)
  const [legendHover, setLegendHover] = useState<string | null>(null)

  // A series version is a terramantle build when its version string is in the authoritative set
  // from detail.versions[].build (never regex-guessed from the `-0.0.N` shape). Those series get
  // a dashed stroke + a "(built)" label so the customer sees adoption at a glance.
  const isBuilt = (version: string) => builtVersions.has(version)

  // Continuous 90-day UTC axis ending today; zero-fill each series onto it.
  const days = useMemo(() => {
    const today = Math.floor(Date.now() / 86400000)
    return Array.from({ length: 90 }, (_, i) => new Date((today - 89 + i) * 86400000).toISOString().slice(0, 10))
  }, [])
  const filled = useMemo(() => series.map((s, idx) => {
    const byDate = new Map(s.points.map((p) => [p.date, p.count]))
    return {
      version: s.version,
      color: s.version === 'other' ? OTHER_COLOR : SERIES_COLORS[idx] ?? OTHER_COLOR,
      counts: days.map((d) => byDate.get(d) ?? 0),
    }
  }), [series, days])

  const visible = filled.filter((s) => !hidden.has(s.version))
  const max = Math.max(1, ...visible.flatMap((s) => s.counts))
  const n = days.length

  // Patched-download share over the 90-day window (the customer-value headline): sum of the
  // patched series' points over the total across all series. 'other' can't be attributed, so
  // it counts only toward the denominator.
  const patchedShare = useMemo(() => {
    let patched = 0
    let total = 0
    for (const s of filled) {
      const sum = s.counts.reduce((a, b) => a + b, 0)
      total += sum
      if (isBuilt(s.version)) patched += sum
    }
    return { patched, total, pct: total > 0 ? Math.round((patched / total) * 100) : 0 }
  }, [filled, builtVersions])

  const VB_W = 300
  const VB_H = 100
  const PAD_Y = 8
  const xAt = (i: number) => (i / (n - 1)) * VB_W
  const yAt = (c: number) => VB_H - PAD_Y - (c / max) * (VB_H - PAD_Y * 2)
  const pathFor = (counts: number[]) =>
    counts.map((c, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(c).toFixed(1)}`).join(' ')

  const shortDate = (d: string) => (d.length >= 10 ? d.slice(5) : d)
  const tickIdx = [0, 22, 45, 67, 89]

  // Hover readout rows: every VISIBLE series' value for the hovered day, largest first.
  const readout = hovered !== null
    ? visible.map((s) => ({ version: s.version, color: s.color, count: s.counts[hovered] ?? 0 }))
        .sort((a, b) => b.count - a.count)
    : []

  function toggle(version: string) {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(version)) next.delete(version)
      else if (next.size < filled.length - 1) next.add(version) // never hide the last visible series
      return next
    })
  }

  const dimmed = (version: string) => legendHover !== null && legendHover !== version

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 108, fontFamily: 'var(--mono)', fontSize: '0.55rem', color: 'var(--text3)', textAlign: 'right', flexShrink: 0 }}>
          <span>{formatCount(max)}</span>
          <span>{formatCount(Math.round(max / 2))}</span>
          <span>0</span>
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {hovered !== null && (
            <div
              style={{
                position: 'absolute',
                // Clamp the anchor to [4%, 96%] so a hover near either end can't push the tooltip
                // past the chart container (which sits inside the modal panel's overflow:hidden).
                left: `${Math.min(96, Math.max(4, (hovered / (n - 1)) * 100))}%`,
                top: 2,
                transform: `translateX(${hovered < n / 6 ? '0' : hovered > (5 * n) / 6 ? '-100%' : '-50%'})`,
                maxWidth: '100%',
                fontFamily: 'var(--mono)',
                fontSize: '0.58rem',
                color: 'var(--text)',
                background: 'var(--bg2)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '4px 7px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <div style={{ color: 'var(--text2)', marginBottom: 2 }}>{shortDate(days[hovered]!)}</div>
              {readout.map((r) => (
                <div key={r.version} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                  <span style={{ color: 'var(--text2)' }}>{r.version === 'other' ? 'other' : `v${r.version}`}{isBuilt(r.version) ? ' (built)' : ''}</span>
                  <strong style={{ marginLeft: 'auto', paddingLeft: 8 }}>{formatCount(r.count)}</strong>
                </div>
              ))}
            </div>
          )}
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="none"
            width="100%"
            height={108}
            style={{ display: 'block', overflow: 'visible' }}
            onMouseLeave={() => setHovered(null)}
          >
            {hovered !== null && (
              <line
                x1={xAt(hovered)} y1={0} x2={xAt(hovered)} y2={VB_H}
                stroke="var(--text3)" strokeWidth={1} vectorEffect="non-scaling-stroke" strokeDasharray="2 2"
              />
            )}
            {visible.map((s) => (
              <path
                key={s.version}
                d={pathFor(s.counts)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                strokeDasharray={isBuilt(s.version) ? '4 3' : undefined}
                vectorEffect="non-scaling-stroke"
                opacity={dimmed(s.version) ? 0.25 : 1}
              />
            ))}
            {hovered !== null && visible.map((s) => (
              <circle
                key={`dot-${s.version}`}
                cx={xAt(hovered)} cy={yAt(s.counts[hovered] ?? 0)} r={3}
                fill={s.color} stroke="var(--bg3)" strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
                opacity={dimmed(s.version) ? 0.25 : 1}
              />
            ))}
            {days.map((d, i) => (
              <rect
                key={`hit-${d}`}
                x={i === 0 ? 0 : (xAt(i - 1) + xAt(i)) / 2}
                y={0}
                width={(i === 0 ? xAt(0) : (xAt(i) - xAt(i - 1)) / 2) + (i === n - 1 ? VB_W - xAt(i) : (xAt(i + 1) - xAt(i)) / 2)}
                height={VB_H}
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
              />
            ))}
          </svg>
          <div style={{ position: 'relative', height: 12, marginTop: 6 }}>
            {tickIdx.map((idx) => (
              <span
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${(idx / (n - 1)) * 100}%`,
                  transform: `translateX(${idx === 0 ? '0' : idx === n - 1 ? '-100%' : '-50%'})`,
                  fontFamily: 'var(--mono)',
                  fontSize: '0.55rem',
                  color: 'var(--text3)',
                  whiteSpace: 'nowrap',
                }}
              >
                {shortDate(days[idx]!)}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Interactive legend: swatch buttons toggle series; hover highlights (dims the rest).
          'other' toggles but never highlight-hovers - it aggregates the tail, not one version. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10, fontFamily: 'var(--mono)', fontSize: '0.66rem' }}>
        {filled.map((s) => {
          const off = hidden.has(s.version)
          return (
            <button
              key={s.version}
              type="button"
              aria-pressed={!off}
              onClick={() => toggle(s.version)}
              onMouseEnter={() => { if (s.version !== 'other') setLegendHover(s.version) }}
              onMouseLeave={() => setLegendHover(null)}
              onFocus={() => { if (s.version !== 'other') setLegendHover(s.version) }}
              onBlur={() => setLegendHover(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer',
                color: off ? 'var(--text3)' : 'var(--text2)',
                textDecoration: off ? 'line-through' : 'none',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: 2, background: s.color, opacity: off ? 0.3 : 1, flexShrink: 0 }} />
              {s.version === 'other' ? 'other' : `v${s.version}`}
              {isBuilt(s.version) && <span style={{ color: 'var(--green)' }}> (built)</span>}
            </button>
          )
        })}
      </div>
      {patchedShare.patched > 0 && (
        <div style={{ marginTop: 10, fontFamily: 'var(--body)', fontSize: '0.72rem', color: 'var(--text2)' }}>
          <strong style={{ color: 'var(--green)' }}>{patchedShare.pct}%</strong> of the last 90 days' downloads
          ({formatCount(patchedShare.patched)} of {formatCount(patchedShare.total)}) were terramantle builds.
        </div>
      )}
    </div>
  )
}
